import * as React from "react";
import type {
  ApprovalState,
  AuditOutcome,
  EvidenceState,
  RunState,
} from "@zworks/console-contracts";
import { Badge, type BadgeProps } from "@/components/ui/badge";

export type DomainStatus =
  | RunState
  | ApprovalState
  | EvidenceState
  | AuditOutcome
  | "pending"
  | "running"
  | "waiting"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "unknown"
  | "approved"
  | "declined"
  | "expired"
  | "verified"
  | "not-yet-due"
  | "unverified"
  | "missing"
  | "denied";

export type StatusBadgeKind = "run" | "approval" | "evidence" | "audit" | "generic";

interface StatusConfig {
  label: string;
  variant: NonNullable<BadgeProps["variant"]>;
}

const runStateMap: Record<RunState, StatusConfig> = {
  pending: { label: "Pending", variant: "info" },
  running: { label: "Running", variant: "info" },
  waiting: { label: "Waiting", variant: "warning" },
  succeeded: { label: "Succeeded", variant: "success" },
  failed: { label: "Failed", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "secondary" },
  unknown: { label: "Unknown effect", variant: "unknown" },
};

const approvalStateMap: Record<ApprovalState, StatusConfig> = {
  pending: { label: "Waiting", variant: "warning" },
  approved: { label: "Approved", variant: "success" },
  declined: { label: "Declined", variant: "danger" },
  expired: { label: "Expired", variant: "secondary" },
};

const evidenceStateMap: Record<EvidenceState, StatusConfig> = {
  verified: { label: "Verified", variant: "success" },
  "not-yet-due": { label: "Not yet due", variant: "secondary" },
  unverified: { label: "Unverified", variant: "warning" },
  missing: { label: "Missing", variant: "danger" },
};

const auditOutcomeMap: Record<AuditOutcome, StatusConfig> = {
  started: { label: "Started", variant: "secondary" },
  succeeded: { label: "Succeeded", variant: "success" },
  denied: { label: "Denied", variant: "warning" },
  failed: { label: "Failed", variant: "danger" },
};

/**
 * Resolves configuration for domain status.
 * Enforces the core invariant: a status NEVER shows success before confirmation.
 */
function resolveConfig(status: DomainStatus, kind?: StatusBadgeKind): StatusConfig {
  if (kind === "run" && status in runStateMap) {
    return runStateMap[status as RunState];
  }
  if (kind === "approval" && status in approvalStateMap) {
    return approvalStateMap[status as ApprovalState];
  }
  if (kind === "evidence" && status in evidenceStateMap) {
    return evidenceStateMap[status as EvidenceState];
  }
  if (kind === "audit" && status in auditOutcomeMap) {
    return auditOutcomeMap[status as AuditOutcome];
  }

  // Generic lookup with safety guard against false success
  if (status === "pending" || status === "running") {
    return { label: status === "pending" ? "Pending" : "Running", variant: "info" };
  }
  if (status === "waiting") {
    return { label: "Waiting", variant: "warning" };
  }
  if (status === "succeeded") {
    return { label: "Succeeded", variant: "success" };
  }
  if (status === "approved" || status === "verified") {
    return {
      label: status === "approved" ? "Approved" : "Verified",
      variant: "success",
    };
  }
  if (status === "failed") {
    return { label: "Failed", variant: "danger" };
  }
  if (status === "declined" || status === "missing") {
    return {
      label: status === "declined" ? "Declined" : "Missing",
      variant: "danger",
    };
  }
  if (status === "denied") {
    return { label: "Denied", variant: "warning" };
  }
  if (status === "unverified") {
    return { label: "Unverified", variant: "warning" };
  }
  if (status === "cancelled" || status === "expired" || status === "not-yet-due") {
    const label =
      status === "cancelled" ? "Cancelled" : status === "expired" ? "Expired" : "Not yet due";
    return { label, variant: "secondary" };
  }
  if (status === "unknown") {
    return { label: "Unknown", variant: "unknown" };
  }

  return { label: String(status), variant: "secondary" };
}

export interface StatusBadgeProps extends Omit<BadgeProps, "children"> {
  status: DomainStatus;
  kind?: StatusBadgeKind;
  label?: string;
}

/**
 * StatusBadge maps domain status to badge variant and label.
 * Critical invariant: Never shows success before confirmation.
 */
export function StatusBadge({ status, kind, label, ...props }: StatusBadgeProps) {
  const config = resolveConfig(status, kind);
  return (
    <Badge variant={config.variant} {...props}>
      {label ?? config.label}
    </Badge>
  );
}
